"use client";

import React from "react";
import { SectionHeader, CTAButton, ContentCard, type ImageComponent, type CardVariant, type CardStyles } from "fansunited-sports-ui";
import { FillImage } from "@/components/ui/FillImage";
import { MediaVariantsProvider } from "@/components/ui/MediaVariantsProvider";
import { getPostImageUrl } from "@/components/ui/PostImage";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { Post } from "@/payload-types";

export interface ArticleGridProps {
    /** Articles to display */
    articles: Post[];
    /** Section title override. Pass `false` to hide the header entirely. */
    title?: string | false;
    /** Show CTA button */
    showCta?: boolean;
    /** ContentCard variant — defaults to "standard" */
    cardVariant?: CardVariant;
    /** Number of columns on desktop — defaults to 3 */
    columns?: 2 | 3 | 4;
    /** Show article description on cards — defaults to true */
    showDescription?: boolean;
    /** Override card styles */
    cardStyles?: CardStyles;
}

const columnClasses = {
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-4",
} as const;

const defaultCardStyles: CardStyles = {
    shadow: "sm",
    borderRadius: "lg",
    typography: {
        title: "text-base font-bold mb-1 line-clamp-2",
        description: "text-sm text-muted-foreground line-clamp-2",
    },
};

/**
 * ArticleGrid - Configurable grid of article cards.
 * Supports variable columns, card variants, optional header, and card style overrides.
 */
export const ArticleGrid: React.FC<ArticleGridProps> = ({
    articles,
    title,
    showCta = true,
    cardVariant = "standard",
    columns = 3,
    showDescription = true,
    cardStyles,
}) => {
    const labels = useLabels();
    const nav = useNavigation();

    if (articles.length === 0) return null;

    const showHeader = title !== false;
    const mergedCardStyles = cardStyles ? { ...defaultCardStyles, ...cardStyles } : defaultCardStyles;

    return (
        <section>
            {showHeader && (
                <SectionHeader
                    entity={{
                        name: (title || labels.homePage.latestNews).toUpperCase(),
                        styles: {
                            typography: {
                                name: "text-sm uppercase font-bold text-foreground tracking-wider",
                            },
                        },
                    }}
                    primaryCta={
                        showCta ? (
                            <CTAButton
                                text={labels.homePage.viewAll}
                                href={nav.articles()}
                                variant="custom"
                                styles={{
                                    container: "text-sm font-medium text-accent hover:text-accent/80 border-none p-0",
                                }}
                            />
                        ) : undefined
                    }
                    styles={{ container: "mb-4" }}
                />
            )}
            <MediaVariantsProvider media={articles.map((post) => post.heroImage)}>
                <div className={`grid ${columnClasses[columns]} gap-4`}>
                    {articles.map((article) => (
                        <ContentCard
                            key={article.id}
                            content={{
                                id: String(article.id),
                                type: article.type === "video" ? "video" : "article",
                                link: nav.post(article),
                                title: article.title,
                                description: showDescription ? article.summary || undefined : undefined,
                                image: {
                                    url: getPostImageUrl(article, "medium"),
                                    alt: article.title,
                                },
                            }}
                            variant={cardVariant}
                            ImageComponent={FillImage as ImageComponent}
                            styles={mergedCardStyles}
                        />
                    ))}
                </div>
            </MediaVariantsProvider>
        </section>
    );
};
